// Shared, fully-local "Surprise Me" meme engine + renderer primitives.
// Used by the Meme Generator (/memes) and the homepage "Meme of the Moment".
// NO AI/LLM/API calls — everything here is deterministic/local ($0 token cost).
import { Banknote, Coins, TrendingUp, TrendingDown, DollarSign } from "lucide-react";

export const FORMATS = {
  square: { id: "square", label: "1:1", w: 1080, h: 1080 },
  portrait: { id: "portrait", label: "9:16", w: 1080, h: 1920 },
  landscape: { id: "landscape", label: "16:9", w: 1920, h: 1080 },
};

export const BACKGROUNDS = [
  { id: "white", label: "White", value: "#FFFFFF" },
  { id: "black", label: "Black", value: "#0D0D0D" },
  { id: "green", label: "Green", value: "#00FF66" },
];
export const bgValue = (id) => (BACKGROUNDS.find((b) => b.id === id) || BACKGROUNDS[1]).value;

export const STICKER_TYPES = [
  { type: "cash", label: "Cash", Icon: Banknote },
  { type: "coin", label: "Coin", Icon: Coins },
  { type: "up", label: "Pump", Icon: TrendingUp },
  { type: "down", label: "Dump", Icon: TrendingDown },
  { type: "dollar", label: "Dollar", Icon: DollarSign },
];

let SEQ = 0;
export const uid = () => `el_${Date.now().toString(36)}_${++SEQ}`;
export const rnd = (a) => a[Math.floor(Math.random() * a.length)];

// Official X handle derived from the single source of truth (settings.xUrl).
export const xHandleFromUrl = (url) => {
  if (!url) return "";
  try {
    const seg = new URL(url).pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const h = seg[seg.length - 1];
    if (!h || ["intent", "home"].includes(h.toLowerCase())) return "";
    return "@" + h.replace(/^@/, "");
  } catch {
    const m = String(url).match(/(?:x|twitter)\.com\/@?([A-Za-z0-9_]{1,15})/i);
    return m ? "@" + m[1] : "";
  }
};
// Append the official @handle to X share text (no-op if not configured).
export const withXHandle = (text, xUrl) => {
  const h = xHandleFromUrl(xUrl);
  return h ? `${text} ${h}` : text;
};

export function mkText(o = {}) {
  return {
    id: uid(), type: "text",
    text: o.text ?? "TEXT",
    x: o.x ?? 540, y: o.y ?? 200,
    w: o.w ?? 900, rot: o.rot ?? 0,
    fontSize: o.fontSize ?? 90,
    bold: o.bold ?? true, italic: o.italic ?? false,
    align: o.align ?? "center",
    color: o.color ?? "#FFFFFF",
    stroke: o.stroke ?? true, strokeColor: o.strokeColor ?? "#0D0D0D",
    shadow: o.shadow ?? false,
  };
}

// Meme text templates — each returns text elements laid out for the given canvas.
export const TEMPLATES = [
  { id: "classic", label: "Classic Meme", boxes: 2, make: (W, H) => ([
    mkText({ text: "TOP TEXT", x: W / 2, y: H * 0.1, fontSize: Math.round(W * 0.08), w: W * 0.9 }),
    mkText({ text: "BOTTOM TEXT", x: W / 2, y: H * 0.9, fontSize: Math.round(W * 0.08), w: W * 0.9 }),
  ]) },
  { id: "topbottom", label: "Top + Bottom", boxes: 2, make: (W, H) => ([
    mkText({ text: "WHEN THE CHART", x: W / 2, y: H * 0.1, fontSize: Math.round(W * 0.07), w: W * 0.9 }),
    mkText({ text: "ONLY GOES DOWN", x: W / 2, y: H * 0.9, fontSize: Math.round(W * 0.07), w: W * 0.9 }),
  ]) },
  { id: "breaking", label: "Breaking News", boxes: 2, make: (W, H) => ([
    mkText({ text: "BREAKING NEWS", x: W / 2, y: H * 0.86, fontSize: Math.round(W * 0.06), w: W * 0.94, color: "#FFFFFF", strokeColor: "#FF3366" }),
    mkText({ text: "we got robbed again", x: W / 2, y: H * 0.94, fontSize: Math.round(W * 0.04), w: W * 0.94, color: "#FFD700" }),
  ]) },
  { id: "pov", label: "POV", boxes: 1, make: (W, H) => ([
    mkText({ text: "POV: you aped the top", x: W / 2, y: H * 0.09, fontSize: Math.round(W * 0.055), w: W * 0.9, align: "center" }),
  ]) },
  { id: "whenyou", label: "When You...", boxes: 1, make: (W, H) => ([
    mkText({ text: "WHEN YOU CHECK YOUR BAG", x: W / 2, y: H * 0.1, fontSize: Math.round(W * 0.06), w: W * 0.9 }),
  ]) },
  { id: "meafter", label: "Me After...", boxes: 1, make: (W, H) => ([
    mkText({ text: "ME AFTER GETTING ROBBED", x: W / 2, y: H * 0.9, fontSize: Math.round(W * 0.06), w: W * 0.9 }),
  ]) },
];
export const templateById = (id) => TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];

export const TONES = [
  { id: "casual", emoji: "😂", short: "Casual" },
  { id: "savage", emoji: "🔥", short: "Savage" },
  { id: "robbed", emoji: "💀", short: "Robbed" },
];

export const SINGLES = {
  casual: ["Me checking my portfolio again…", "They said it was going up.", "This was supposed to be easy.", "Me after buying the top.", "Where did the money go?", "I trusted the chart.", "Nobody warned me.", "Just one more dip, I said."],
  savage: ["WE WERE ROBBED.", "When the pump turns into a dump.", "Down bad. Again.", "Exit liquidity: me.", "Rugged in broad daylight.", "My bags have bags.", "The chart lied to me."],
  robbed: ["ABSOLUTELY ROBBED.", "GONE. REDUCED TO ZEROS.", "THEY TOOK EVERYTHING.", "PORTFOLIO: A CRIME SCENE.", "REKT BEYOND REPAIR.", "IT'S ALL GONE.", "ROBBED IN 4K."],
};
export const PAIRS = {
  casual: [["THEY SAID", "IT WAS GOING UP"], ["I TRUSTED", "THE CHART"], ["ME CHECKING", "MY BAGS AGAIN"], ["IT WAS SUPPOSED", "TO BE EASY"], ["JUST A DIP", "THEY SAID"]],
  savage: [["WHEN THE PUMP", "BECOMES A DUMP"], ["BOUGHT THE TOP", "SOLD THE BOTTOM"], ["MY BAGS", "HAVE BAGS"], ["WEN MOON?", "NEVER"], ["GREEN CANDLE?", "IN MY DREAMS"]],
  robbed: [["WE WERE", "ROBBED"], ["THEY TOOK", "EVERYTHING"], ["PORTFOLIO GONE", "REDUCED TO ASH"], ["ROBBED", "IN BROAD DAYLIGHT"], ["IT'S ALL", "GONE"]],
};
export const POSE_HINTS = {
  chart: "I trusted the chart.", moneybag: "Where did the money go?", crying: "Nobody warned me.",
  jump: "They said it was going up.", throne: "King of an empty bag.", crate: "Sitting on my last assets.",
  stare: "Not amused. Just robbed.", pointing: "You did this.", shrug: "It is what it is.",
};

export const pickCaption = (tone, nBoxes, poseId) => {
  const t = SINGLES[tone] ? tone : "savage";
  if (nBoxes >= 2) return [...rnd(PAIRS[t])];
  if (POSE_HINTS[poseId] && Math.random() < 0.45) return [POSE_HINTS[poseId]];
  return [rnd(SINGLES[t])];
};

export const INTERACTION_CAPTIONS = {
  faceoff: ["WE NEED TO TALK.", "EXPLAIN YOURSELF.", "WHO SOLD?", "LOOK AT MY BAGS."],
  argument: ["YOU SAID IT WAS SAFE.", "NOT MY FAULT.", "YOU APED FIRST.", "WE'RE BOTH BROKE."],
  point_react: ["YOU DID THIS.", "I WARNED YOU.", "IT WAS YOUR CALL.", "BLAME HIM."],
  laugh: ["WE'RE SO COOKED.", "MIGHT AS WELL LAUGH.", "IT'S FINE. IT'S FINE.", "REKT TOGETHER."],
  comfort: ["BRO…", "IT'S GOING TO BE OKAY.", "WE'LL MAKE IT BACK.", "LET IT OUT."],
  winner_loser: ["GG. NEXT.", "IT'S OVER.", "SKILL ISSUE.", "STAY POOR."],
  shock: ["NO WAY.", "WHAT?!", "IT'S GONE?!", "ANOTHER RUG?!"],
};

// Polished $ROBBED PUMP/DUMP illustrated sticker (candlesticks + bold arrow + lettering).
// Transparent, no frame. Drawn centred at (0,0) within box `s`.
function drawPumpDump(ctx, up, s) {
  const ink = "#0D0D0D";
  const main = up ? "#00FF66" : "#FF3366";
  const cols = up ? ["#12b34a", "#00FF66", "#39ff7a", "#00FF66"] : ["#c0392b", "#FF3366", "#ff7a45", "#FF3366"];
  ctx.save();
  ctx.translate(0, -s * 0.04);
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  // candlesticks
  const xs = [-0.40, -0.23, -0.06, 0.11];
  const heights = up ? [0.16, 0.24, 0.34, 0.46] : [0.46, 0.34, 0.24, 0.16];
  const base = 0.30 * s, bw = 0.10 * s;
  xs.forEach((xf, i) => {
    const x = xf * s, h = heights[i] * s, top = base - h;
    ctx.strokeStyle = ink; ctx.lineWidth = s * 0.02;
    ctx.beginPath(); ctx.moveTo(x, top - 0.05 * s); ctx.lineTo(x, base + 0.04 * s); ctx.stroke();
    ctx.fillStyle = cols[i]; ctx.strokeStyle = ink; ctx.lineWidth = s * 0.03;
    ctx.beginPath(); ctx.rect(x - bw / 2, top, bw, h); ctx.fill(); ctx.stroke();
  });
  // arrow
  const ax0 = -0.44 * s, ay0 = up ? 0.24 * s : -0.24 * s;
  const ax1 = 0.38 * s, ay1 = up ? -0.30 * s : 0.30 * s;
  // speed lines behind the tail
  ctx.strokeStyle = main; ctx.globalAlpha = 0.45; ctx.lineWidth = s * 0.03;
  for (let i = 0; i < 3; i++) { const yy = ay0 + (i - 1) * 0.1 * s; ctx.beginPath(); ctx.moveTo(ax0 - 0.13 * s, yy); ctx.lineTo(ax0 - 0.02 * s, yy); ctx.stroke(); }
  ctx.globalAlpha = 1;
  ctx.strokeStyle = ink; ctx.lineWidth = s * 0.17;
  ctx.beginPath(); ctx.moveTo(ax0, ay0); ctx.lineTo(ax1, ay1); ctx.stroke();
  ctx.strokeStyle = main; ctx.lineWidth = s * 0.10;
  ctx.beginPath(); ctx.moveTo(ax0, ay0); ctx.lineTo(ax1, ay1); ctx.stroke();
  const ang = Math.atan2(ay1 - ay0, ax1 - ax0), hl = 0.22 * s;
  ctx.fillStyle = main; ctx.strokeStyle = ink; ctx.lineWidth = s * 0.03;
  ctx.beginPath();
  ctx.moveTo(ax1, ay1);
  ctx.lineTo(ax1 - hl * Math.cos(ang - 0.5), ay1 - hl * Math.sin(ang - 0.5));
  ctx.lineTo(ax1 - hl * Math.cos(ang + 0.5), ay1 - hl * Math.sin(ang + 0.5));
  ctx.closePath(); ctx.fill(); ctx.stroke();
  // lettering
  ctx.font = `900 ${s * 0.22}px Unbounded, sans-serif`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.lineJoin = "round"; ctx.lineWidth = s * 0.06; ctx.strokeStyle = ink; ctx.fillStyle = main;
  const label = up ? "PUMP" : "DUMP", ty = 0.46 * s;
  ctx.strokeText(label, 0, ty); ctx.fillText(label, 0, ty);
  ctx.restore();
}

// Vector stickers — in-brand, scalable, no external assets. Drawn centred at (0,0) at box size `sz`.
export function drawStickerShape(ctx, type, sz) {
  ctx.lineJoin = "round"; ctx.lineCap = "round";
  const ink = "#0D0D0D", lime = "#00FF66";
  if (type === "cash") {
    // stacked bills with neon-lime $ROBBED accents
    ctx.save(); ctx.translate(0, -sz * 0.02);
    const bw = sz * 0.82, bh = sz * 0.44;
    const bills = [{ dx: -sz * 0.1, dy: sz * 0.16, r: -8 }, { dx: sz * 0.04, dy: 0.0, r: 5 }, { dx: -sz * 0.02, dy: -sz * 0.16, r: -3 }];
    bills.forEach((b, i) => {
      ctx.save(); ctx.translate(b.dx, b.dy); ctx.rotate((b.r * Math.PI) / 180);
      ctx.fillStyle = i === 2 ? "#3fae4a" : "#2e8b3a"; ctx.strokeStyle = ink; ctx.lineWidth = sz * 0.045;
      ctx.beginPath(); ctx.rect(-bw / 2, -bh / 2, bw, bh); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = lime; ctx.lineWidth = sz * 0.02;
      ctx.beginPath(); ctx.ellipse(0, 0, bw * 0.26, bh * 0.34, 0, 0, Math.PI * 2); ctx.stroke();
      if (i === 2) { ctx.fillStyle = lime; ctx.font = `900 ${sz * 0.3}px Unbounded, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.lineWidth = sz * 0.04; ctx.strokeStyle = ink; ctx.strokeText("$", 0, sz * 0.01); ctx.fillText("$", 0, sz * 0.01); }
      ctx.restore();
    });
    ctx.restore();
  } else if (type === "coin") {
    // metallic crypto coin, neon-lime rim + bold $
    const r = sz * 0.46;
    const g = ctx.createLinearGradient(-r, -r, r, r);
    g.addColorStop(0, "#7CFF9E"); g.addColorStop(0.5, "#00FF66"); g.addColorStop(1, "#0aa845");
    ctx.fillStyle = g; ctx.strokeStyle = ink; ctx.lineWidth = sz * 0.06;
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.strokeStyle = "#0D0D0D"; ctx.lineWidth = sz * 0.03;
    ctx.beginPath(); ctx.arc(0, 0, r * 0.78, 0, Math.PI * 2); ctx.stroke();
    // highlight arc
    ctx.strokeStyle = "rgba(255,255,255,0.7)"; ctx.lineWidth = sz * 0.03;
    ctx.beginPath(); ctx.arc(0, 0, r * 0.62, Math.PI * 1.05, Math.PI * 1.55); ctx.stroke();
    ctx.fillStyle = ink; ctx.font = `900 ${sz * 0.56}px Unbounded, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("$", 0, sz * 0.03);
  } else if (type === "up" || type === "down") {
    drawPumpDump(ctx, type === "up", sz);
  } else if (type === "dollar") {
    // oversized dynamic $ with drop shadow + neon fill
    ctx.save(); ctx.rotate((-8 * Math.PI) / 180);
    ctx.font = `900 ${sz * 1.05}px Unbounded, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = ink; ctx.fillText("$", sz * 0.06, sz * 0.07); // shadow
    ctx.lineJoin = "round"; ctx.lineWidth = sz * 0.09; ctx.strokeStyle = ink; ctx.strokeText("$", 0, 0);
    const g = ctx.createLinearGradient(0, -sz * 0.5, 0, sz * 0.5);
    g.addColorStop(0, "#9dffb3"); g.addColorStop(1, "#00FF66");
    ctx.fillStyle = g; ctx.fillText("$", 0, 0);
    ctx.restore();
  }
}

export function wrapLines(ctx, text, maxW) {
  const out = [];
  for (const raw of String(text).split("\n")) {
    const words = raw.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(""); continue; }
    let line = "";
    for (const w of words) {
      const t = line ? `${line} ${w}` : w;
      if (ctx.measureText(t).width > maxW && line) { out.push(line); line = w; } else line = t;
    }
    if (line) out.push(line);
  }
  return out.length ? out : [""];
}

export function drawTextElement(ctx, e) {
  ctx.save();
  ctx.translate(e.x, e.y);
  if (e.rot) ctx.rotate((e.rot * Math.PI) / 180);
  const weight = e.bold ? 900 : 600;
  const style = e.italic ? "italic " : "";
  ctx.font = `${style}${weight} ${e.fontSize}px Unbounded, sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign = e.align;
  const maxW = e.w;
  const lines = wrapLines(ctx, e.text, maxW);
  const lh = e.fontSize * 1.14;
  const blockH = lines.length * lh;
  const anchorX = e.align === "left" ? -maxW / 2 : e.align === "right" ? maxW / 2 : 0;
  let yy = -blockH / 2 + lh / 2;
  for (const l of lines) {
    if (e.shadow) { ctx.shadowColor = "rgba(0,0,0,0.55)"; ctx.shadowBlur = e.fontSize * 0.12; ctx.shadowOffsetX = e.fontSize * 0.04; ctx.shadowOffsetY = e.fontSize * 0.06; }
    if (e.stroke) { ctx.lineJoin = "round"; ctx.lineWidth = e.fontSize * 0.16; ctx.strokeStyle = e.strokeColor; ctx.strokeText(l, anchorX, yy); }
    ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    ctx.fillStyle = e.color; ctx.fillText(l, anchorX, yy);
    yy += lh;
  }
  ctx.restore();
}

// Render a Meme-of-the-Moment config (from the server) onto a canvas. Fully local.
export function renderMomentConfig(canvas, cfg, charImg) {
  if (!canvas || !cfg) return;
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = bgValue(cfg.bgId);
  ctx.fillRect(0, 0, W, H);
  // character (contain-fit, ~72% height, centred)
  if (charImg && charImg.naturalWidth) {
    const targetH = H * 0.72;
    const asp = charImg.naturalWidth / charImg.naturalHeight;
    const h = targetH, w = targetH * asp;
    ctx.drawImage(charImg, W / 2 - w / 2, H / 2 - h / 2, w, h);
  }
  // sticker
  if (cfg.stickerType) {
    const sz = Math.round(Math.min(W, H) * 0.2);
    ctx.save();
    ctx.translate(W * 0.74, H * 0.3);
    if (cfg.stickerRot) ctx.rotate((cfg.stickerRot * Math.PI) / 180);
    drawStickerShape(ctx, cfg.stickerType, sz);
    ctx.restore();
  }
  // template text with caption injected
  const tpl = templateById(cfg.templateId);
  const texts = tpl.make(W, H);
  const cap = cfg.caption || [];
  texts.forEach((t, i) => { t.text = cap[i] ?? cap[cap.length - 1] ?? t.text; });
  texts.forEach((t) => drawTextElement(ctx, t));
}
